import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, withLatestFrom } from 'rxjs/operators';
import { ApiService } from 'src/app/core/backend/api.service';
import { CryptoService } from 'src/app/core/firebase/crypto.service';
import { GameSettingsService } from 'src/app/core/firebase/gameSettings.service';
import { AppState } from 'src/app/core/state/app.state';
import { CachingStatus, Collection, CryptoState, NftAsset } from 'src/app/core/state/crypto/crypto.state';
import { ProfileStateDispatcher } from 'src/app/core/state/profile/profile.dispatcher';
import { SopoLoadingService } from 'src/app/shared/services/sopo-loading/sopo-loading.service';
import { SwiperComponent } from 'swiper/angular';
import { SwiperEvents, SwiperOptions } from 'swiper/types';
import { NftSelectorComponent } from '../nft-selector/nft-selector.component';
import { SOPO_DIALOG_DATA, SopoDialogRef, SopoDialogSettings } from 'src/app/sopo-popup/interfaces/dialog';
import { SearchbarCustomEvent } from '@ionic/angular';
import { SopoPopupService } from 'src/app/sopo-popup/service/sopo-popup.service';
import { CryptoIsLoggedInType, CryptoStateDispatcher } from 'src/app/core/state/crypto/crypto.dispatcher';
import { ThemeService } from 'src/app/core/firebase/theme.service';

@Component({
    selector: 'app-my-wallet',
    templateUrl: './my-wallet.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./my-wallet.component.scss'],
    animations: [
        trigger('inOutAnimation', [
            transition(':enter', [
                style({
                    height: 0,
                    maxHeight: 0,
                    minHeight: 0,
                    opacity: 0
                }),
                animate('0.2s ease-out', style({
                    height: 40,
                    maxHeight: 40,
                    minHeight: 40,
                    opacity: 1
                }))
            ]),
            transition(':leave', [
                style({
                    height: 40,
                    maxHeight: 40,
                    minHeight: 40,
                    opacity: 1
                }),
                animate('0.2s ease-in', style({
                    height: 0,
                    maxHeight: 0,
                    minHeight: 0,
                    opacity: 0
                }))
            ])
        ])
    ],
    standalone: false
})
export class MyWalletComponent implements OnInit {
  @ViewChild('nftSlide') nftSlides: SwiperComponent;
  segment: 'all' | 'favorites' = 'all';
  nftListInCollection$: Observable<NftCollection[] | null>;
  favoriteNfts$: Observable<NftAsset[] | null>;
  sliderOptions: SwiperOptions = {
      slidesPerView: 1,
      pagination: false,
  }

  swiperOpen: { [key: number]: boolean} = {};
  swiperEnabled: { [key: number]: boolean} = {};
  nftPreloaded: { [key: string]: boolean | null} = {};

  nftLoadingStatus$: Observable<CachingStatus>;
  sopoDialogRef: SopoDialogRef = new SopoDialogRef();
  isInProfilePage: boolean = false;
  filterValue: string = '';
  linearGradientStart = '#870b38';
  linearGradientEnd = '#350031'

  public isCryptoLoggedIn$: Observable<CryptoIsLoggedInType>;

  constructor(
      private profileDispatcher: ProfileStateDispatcher,
      private gameSettingsService: GameSettingsService,
      private loadingCtrl: SopoLoadingService,
      private store: Store<AppState>,
      private cryptoService: CryptoService,
      private ref: ChangeDetectorRef,
      private apiService: ApiService,
      private sopoPopupService: SopoPopupService,
      private cryptoDispatcher: CryptoStateDispatcher,
      private themeService: ThemeService,
      @Inject(SOPO_DIALOG_DATA) private data: { isInProfilePage: boolean }
    ) { 
        if (data) this.isInProfilePage = data.isInProfilePage;
    }

    asSearchbarCustomEvent(event: Event) {
        return event as SearchbarCustomEvent;
    }

    onSearchChange(event: SearchbarCustomEvent): void {
        this.filterValue = event.detail.value;
    }

  public get cachingStatus(): typeof CachingStatus {
    return CachingStatus; 
  }

  showStatus(cachingStatus: CachingStatus): boolean {
      if (this.segment === 'favorites') return false;
      if (cachingStatus === CachingStatus.CACHING) return true;
      if (cachingStatus === CachingStatus.CACHING_IN_WAITING) return true;
      return false;
  }

  ngOnInit() {
      const themePath = this.themeService.themePath;
      if (themePath === 'black-gold-theme') {
        this.linearGradientStart = 'rgb(153 119 0) 0%';
        this.linearGradientEnd = 'rgb(94 83 23) 100%';
      }
      this.nftListInCollection$ = this.store.select('crypto').pipe(
        map(state => state.nftAssets),
        withLatestFrom(this.store.select('crypto')),
        map(this.groupNftsByCollection),
        map(collections => collections.length > 0 ? collections : null),
      );
      this.favoriteNfts$ = this.cryptoService.getFavorites().pipe(
          withLatestFrom(this.store.select('crypto').pipe(map(crypto => crypto.nftAssets))),
          map(([favorites, ownedNfts]) => {
              const canFindInOwned = (nft: NftAsset) => {
                  return ownedNfts.find(owned => this.getNftKey(owned) === this.getNftKey(nft)) !== undefined;
              }
              return favorites.filter(favorite => canFindInOwned(favorite));
          }),
          map(favorites => favorites.length > 0 ? favorites : null)
      );
      this.nftLoadingStatus$ = this.store.select('crypto').pipe(
          map(state => state.loadingStatus),
          distinctUntilChanged()
      );
      this.isCryptoLoggedIn$ =  this.cryptoDispatcher.isLoggedIn();
  }

  groupNftsByCollection([nftAssets, cryptoState]: [NftAsset[], CryptoState]): NftCollection[] { 
      type NftsByCollection = Collection & {elements: NftAsset[]}
      const mappedAssets: NftsByCollection[] = [];
      nftAssets.forEach(asset => {
          const collectionIndex = mappedAssets.findIndex(element => element.address === asset.collectionAddress);
          if (collectionIndex > -1) {
              mappedAssets[collectionIndex].elements.push({...asset})
          } else {
              let collectionFromOS = cryptoState.collections.find(col => col.address === asset.collectionAddress);
              const newCollection: NftsByCollection = {
                  imageUrl: '',
                  bannerImageUrl: '',
                  description: '',
                  elements: [{...asset}],
                  name: asset.collectionName,
                  ...collectionFromOS,
                  address: asset.collectionAddress,
              };
              mappedAssets.push(newCollection);
          }
      });

      const splitAndFillNFTs = ( nfts: NftAsset[], isLoading: boolean ): NftGroup[] => {
          let startArray = [...nfts];
          let finishArray: NftGroup[] = [];
          while(startArray.length) {
              const needBlankRow = startArray.length === 4 && isLoading;
              const newGroup = startArray.splice(0,4);
              finishArray.push({
                  0: newGroup[0],
                  1: newGroup[1] ? newGroup[1] : null,
                  2: newGroup[2] ? newGroup[2] : null,
                  3: newGroup[3] ? newGroup[3] : null,
              })
              if (needBlankRow) finishArray.push({
                  0: null,
                  1: null,
                  2: null,
                  3: null,
              })
          }
          if (finishArray.length === 0) {
              finishArray.push({
                  0: null,
                  1: null,
                  2: null,
                  3: null,
              })
          }
          return finishArray;
      }
      const response = mappedAssets.map((assets: NftsByCollection, index: number) => {
          return {
              ...assets,
              elements:  splitAndFillNFTs([...assets.elements], false)
          }
      }).sort((a, b) => {
          if (a.name > b.name) {
              return 1;
          }
          if (b.name > a.name) {
              return -1;
          }
          return 0;
      });
      return response;
  }

  close() {
    if(this.sopoDialogRef) this.sopoDialogRef.close(true);
  }

  segmentChanged(event: CustomEvent<{value: 'all' | 'favorites'}>) {
      this.segment = event.detail.value;
  }

  trackByUrl(index: number, data: NftAsset) {
      return data.imageUrl;
  }

  public async select( nft: NftAsset, event: any ): Promise<void> {
    if (event.srcElement.getAttribute('disabled') === 'true') return;
    event.srcElement.setAttribute('disabled', true);

    const selectedNft$ = this.store.select('crypto').pipe(
        map(state => state.nftAssets),
        map(assets => assets.find(asset => asset.collectionTokenId === nft.collectionTokenId && asset.collectionAddress === nft.collectionAddress)),
    );

    if (!this.canShowNft(nft)) return;
    const isFavorite = await this.cryptoService.checkNftAsFavorite(nft);

    const dialogSettings: SopoDialogSettings = { 
        data: {
            isFavorite,
            nftData$: selectedNft$,
        },
        disableScroll: true,
    };
    if (!this.isInProfilePage) dialogSettings.zIndex = 2002;

    const sopoDialogRef = this.sopoPopupService.openDialog('app', NftSelectorComponent, dialogSettings);
    sopoDialogRef.afterClosed.subscribe(async (value: string) => {
        event.srcElement.setAttribute('disabled', false);
        switch(value) {
            case 'confirm':
                await this.changeAvatarToNft(nft);
                break;
            case 'refresh':
                this.ref.markForCheck();
        }
    });
  }

  async changeAvatarToNft(nft: NftAsset) {
      const userId = await this.profileDispatcher.getMyID();
      this.loadingCtrl.present('api');
      try {
          await this.apiService.onSetNftAsAvatar({ collectionTokenId: nft.collectionTokenId, collectionAddress: nft.collectionAddress }, userId);
          this.gameSettingsService.showNftThemeSettingsAlertIfNeeded(false);
      } catch (error) {
          console.error(error);
      } finally {
          this.loadingCtrl.dismiss('api');
      }
  }

  toggleSwiper(index: number, collection: NftCollection) {
      const needToOpen = this.swiperOpen[index] ? false : true;
      Object.keys(this.swiperOpen).forEach(currentIndex => {
          if (+currentIndex !== index && needToOpen) this.closeSwiper(+currentIndex);
          if (+currentIndex === index) {
              if (needToOpen) this.openSwiper(index, collection);
              else this.closeSwiper(index);
          }
      });
      if (this.swiperOpen[index] === undefined) this.openSwiper(index, collection);
  }

  openSwiper(index: number, collection: NftCollection) {
      this.swiperOpen[index] = true;
      this.swiperEnabled[index] = true;
      this.preloadNfts(collection, index);
  }

  closeSwiper(index: number) {
      this.swiperOpen[index] = false;
      setTimeout(() => {
          this.swiperEnabled[index] = false;
          this.ref.markForCheck();
      }, 500)
  }

  onSlideChange(eventParams: Parameters<SwiperEvents['slideChange']>, collection: NftCollection, collectionIndex: number) {
      const [swiper] = eventParams;
      this.preloadNfts(collection, collectionIndex, swiper.snapIndex);
  }

  preloadNfts(collection: NftCollection, collectionIndex: number, groupIndex = 0) {
      for (let nft of (Object.values(collection.elements[groupIndex]) as (NftAsset | null)[])) {
          if (!this.swiperOpen[collectionIndex]) return;
          if (nft === null) break;
          if (this.nftPreloaded[this.getNftKey(nft)]) break;
          this.preloadNft(nft);
      }
  }
  async preloadNft(nft: NftAsset) {
      const nftKey = this.getNftKey(nft);
      try {
          this.changeNftPreloadStatus(nftKey, 'load_start');
          await this.cryptoService.preloadNft(nft);
          this.changeNftPreloadStatus(nftKey, 'loaded');
      } catch(e) {
          console.error(e);
          this.changeNftPreloadStatus(nftKey, 'failed');
      }
  }

  getNftKey(nft: NftAsset) {
      return `${nft.collectionAddress}_${nft.collectionTokenId}`;
  }

  changeNftPreloadStatus(nftKey: string, status: 'load_start' | 'loaded' | 'failed') {
      switch(status) {
          case 'load_start':
              this.nftPreloaded[nftKey] = false;
              break;
          case 'loaded':
              this.nftPreloaded[nftKey] = true;
              break;
          case 'failed':
              this.nftPreloaded[nftKey] = null;
              break;
      };
      this.ref.detectChanges();
  }

  canShowNft(nft: NftAsset) {
      return nft && this.nftPreloaded[this.getNftKey(nft)];
  }

  getNftImageAsBackground(nft: NftAsset) {
      if (this.canShowNft(nft)) return {backgroundImage: `url(${nft.imageUrl})`};
      if (this.needToShowLoading(nft)) return {backgroundImage: `url(assets/img/load.png)`};
      if (this.segment === 'favorites' && this.nftPreloaded[this.getNftKey(nft)] === undefined) this.preloadNft(nft);
      if (this.preloadErrored(nft)) return {backgroundImage: `url(${GameSettingsService.getSopoBrokenImageUrl('x-small')})`};
      else return {}
  }

  needToShowLoading(nft: NftAsset) {
      return nft && this.nftPreloaded[this.getNftKey(nft)] !== undefined && this.nftPreloaded[this.getNftKey(nft)] === false;
  }

  preloadErrored(nft: NftAsset) {
      return nft && this.nftPreloaded[this.getNftKey(nft)] !== undefined && this.nftPreloaded[this.getNftKey(nft)] === null;
  }

    trackCollection(index: number, collection: NftCollection) {
        return collection.address;
    }

  get titleStyle() {
      return {
          'font-style': 'unset',
          'font-weight': 'bold',
          'text-shadow': '0px 2px 7px #000000',
          'font-size': '17px',
          'text-align': 'center',
      }
  }
}

interface NftCollection extends Collection {
  elements: NftGroup[];
}

export interface NftGroup {
  0: NftAsset | null,
  1: NftAsset | null,
  2: NftAsset | null,
  3: NftAsset | null
}

